import { useEffect, useState } from 'react';
import { Cookie, Settings2, Check, X } from 'lucide-react';
import { getConsent, setConsent } from '@/lib/consent';

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [statistics, setStatistics] = useState(false);

  useEffect(() => {
    if (!getConsent()) setOpen(true);
    const reopen = () => { setShowPrefs(true); setOpen(true); const c = getConsent(); setStatistics(!!(c && c.statistics)); };
    window.addEventListener('nb-open-cookie-settings', reopen);
    return () => window.removeEventListener('nb-open-cookie-settings', reopen);
  }, []);

  const save = (statsValue) => {
    setConsent({ statistics: statsValue });
    setOpen(false);
    setShowPrefs(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => window.dispatchEvent(new Event('nb-open-cookie-settings'))}
        className="fixed bottom-4 left-4 z-[60] w-11 h-11 rounded-full backdrop-blur-xl bg-white/70 dark:bg-white/10 border border-white/30 dark:border-white/15 shadow-lg flex items-center justify-center text-foreground/70 hover:text-[hsl(210,100%,56%)] hover:scale-105 transition-all"
        title="Cookie-Einstellungen"
        data-testid="cookie-settings-fab"
      >
        <Cookie className="w-5 h-5" />
      </button>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px]" aria-hidden="true" />
      <div
        className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-4 flex justify-center"
        role="dialog"
        aria-label="Cookie-Einstellungen"
        data-testid="cookie-banner"
      >
        <div className="w-full max-w-2xl rounded-2xl backdrop-blur-2xl bg-white/85 dark:bg-[hsl(220,20%,11%)]/90 border border-white/40 dark:border-white/15 shadow-[0_8px_40px_rgba(0,0,0,0.35)] overflow-hidden">
          {/* accent top line */}
          <div className="h-1 bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)]" />
          <div className="p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] flex items-center justify-center text-white">
                <Cookie className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold text-foreground">Wir respektieren deine Privatsphäre</h2>
                <p className="text-sm text-foreground/70 mt-1 leading-relaxed">
                  Wir verwenden Cookies bzw. ähnliche Technologien. <strong>Notwendige</strong> sind für den Betrieb der Seite erforderlich.
                  Für anonyme <strong>Statistiken</strong> (Seitenaufrufe, Klicks) bitten wir um deine Einwilligung – nur mit deiner Zustimmung.
                  Mehr dazu in der{' '}
                  <a href="/privacy" className="text-[hsl(210,100%,56%)] hover:underline">Datenschutzerklärung</a>.
                </p>
              </div>
            </div>

            {showPrefs && (
              <div className="mt-4 space-y-2" data-testid="cookie-prefs">
                <div className="flex items-center justify-between p-3 rounded-xl bg-foreground/5 border border-white/10">
                  <div>
                    <p className="text-sm font-medium text-foreground">Notwendig</p>
                    <p className="text-xs text-foreground/50">Immer aktiv – für Grundfunktionen der Website.</p>
                  </div>
                  <span className="text-xs font-medium text-foreground/40 px-3 py-1 rounded-full bg-foreground/10">Aktiv</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl bg-foreground/5 border border-white/10">
                  <div>
                    <p className="text-sm font-medium text-foreground">Statistik</p>
                    <p className="text-xs text-foreground/50">Anonyme Reichweitenmessung (First-Party, kein Tracking durch Dritte).</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStatistics((v) => !v)}
                    className={`relative w-12 h-7 rounded-full transition-colors ${statistics ? 'bg-[hsl(210,100%,56%)]' : 'bg-foreground/20'}`}
                    role="switch"
                    aria-checked={statistics}
                    data-testid="cookie-statistics-toggle"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${statistics ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:gap-2 sm:items-center">
              <button
                type="button"
                onClick={() => save(true)}
                className="order-1 sm:order-3 flex-none sm:flex-1 min-h-[56px] h-14 sm:min-h-[44px] sm:h-11 text-base sm:text-sm rounded-2xl sm:rounded-xl bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] text-white font-medium shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                data-testid="cookie-accept-all"
              >
                <Check className="w-5 h-5 sm:w-4 sm:h-4" /> Alle akzeptieren
              </button>
              <button
                type="button"
                onClick={() => save(false)}
                className="order-2 flex-none sm:flex-1 min-h-[56px] h-14 sm:min-h-[44px] sm:h-11 text-base sm:text-sm rounded-2xl sm:rounded-xl border border-foreground/20 text-foreground font-medium hover:bg-foreground/5 transition-colors flex items-center justify-center gap-2"
                data-testid="cookie-reject-all"
              >
                <X className="w-5 h-5 sm:w-4 sm:h-4" /> Nur notwendige
              </button>
              {showPrefs ? (
                <button
                  type="button"
                  onClick={() => save(statistics)}
                  className="order-3 sm:order-1 flex-none sm:flex-1 min-h-[56px] h-14 sm:min-h-[44px] sm:h-11 text-base sm:text-sm rounded-2xl sm:rounded-xl border border-[hsl(210,100%,56%)]/40 text-[hsl(210,100%,56%)] font-medium hover:bg-[hsl(210,100%,56%)]/10 transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-save-prefs"
                >
                  Auswahl speichern
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowPrefs(true)}
                  className="order-3 sm:order-1 flex-none sm:flex-1 min-h-[56px] h-14 sm:min-h-[44px] sm:h-11 text-base sm:text-sm rounded-2xl sm:rounded-xl border border-foreground/20 text-foreground/80 font-medium hover:bg-foreground/5 transition-colors flex items-center justify-center gap-2"
                  data-testid="cookie-customize"
                >
                  <Settings2 className="w-5 h-5 sm:w-4 sm:h-4" /> Einstellungen
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
